import {
  Alert,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import DataLoadingError from '@components/common/DataLoadingError';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { participantAttendedSession, sortByParticipantName } from '@components/Pages/Events/Sessions/sessionFunctions';
import { NILL } from '@lib/constants';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';

const SessionParticipantsTable = ({ session, query }) => {

  const { data: participants, ...participantsQuery } = query;
  if (participants) {
    participants.sort(sortByParticipantName);
  }

  const isLoading = participantsQuery.isLoading;

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign="center">
        <CircularProgress/>
      </Grid>
    );
  }

  if (participantsQuery.isError && participantsQuery.error.status !== 404) {
    return <DataLoadingError title={'Failed to load participants!'} errors={participantsQuery.error.errors}/>;
  }

  // Has any participant data been recorded for the event?
  const eventHasParticipants = participants?.length > 0;

  // Has any participant data been recorded for the session?
  const anySessionParticipants = participants?.filter(p => p.participantAttendance.find(a => a.session_id === session.id));

  // "Need to upload" message appears if participant data has never been entered for the session OR the event.
  const needToUpload = !eventHasParticipants || (anySessionParticipants.length === 0 && session.uploadLogs?.length === 0);

  return (
    <div style={{ marginTop: '32px' }} data-cy="participants-section">

      {needToUpload && (
        <Alert severity="info" sx={{ marginBottom: '16px' }} data-cy="participant-upload-instructions">
          <ul style={{ margin: 0 }}>
            <li style={{ fontSize: '16px' }}>If you collected participant data on your iPad, go to the Past Sessions
              screen on your iPad and upload your session data.
            </li>
            <li style={{ fontSize: '16px' }}>If you collected participant data on a sign in sheet,
              click &quot;Complete Session&quot; and enter the participant data you collected.
            </li>
            <li style={{ fontSize: '16px' }}>If no one showed up for this session, click &quot;Cancel
              Session&quot; and select &quot;No participants showed up&quot;</li>
          </ul>
        </Alert>
      )}

      {eventHasParticipants && (
        <Table sx={{ marginBottom: '24px' }} data-cy="participants-table">
          <TableHead>
            <TableHeadRow>
              <TableCell component="th">Name</TableCell>
              <TableCell component="th" style={{ textAlign: 'center' }}>Demographics Collected?</TableCell>
              <TableCell component="th" style={{ textAlign: 'center' }}>Session Attendance</TableCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {participants.map(participant => (
              <TableRow key={participant.id}>
                <NameCell>
                  <Grid container>
                    <Grid item xs={8} data-cy="participant-name">
                      {participant.first_name} {participant.middle_name} {participant.last_name}
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      {/* TODO(SEMS-1073): delete link */}
                    </Grid>
                  </Grid>
                </NameCell>
                <DataCell>
                  {participant.demographics_collected ?
                    (
                      <Tooltip title="Collected">
                        <CheckCircleIcon color="success"/>
                      </Tooltip>)
                    :
                    (
                      <Tooltip title="Not collected">
                        <ErrorIcon color="yellow"/>
                      </Tooltip>
                    )}
                </DataCell>
                <DataCell>
                  <AttendanceIcon session={session} participant={participant}/>
                </DataCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!eventHasParticipants && (
        <Typography variant="body1" data-cy="participants-empty">
          There are no participants.
        </Typography>
      )}
    </div>
  );
};

const TableHeadRow = styled(TableRow)({
  '& .MuiTableCell-root': {
    backgroundColor: '#f4f4f4',
    borderColor: '#0000001f',
  },
});

const NameCell = styled(TableCell)({
  backgroundColor: '#f4f4f4',
  borderColor: '#0000001f',
  minWidth: '250px',
});

const DataCell = styled(TableCell)({
  backgroundColor: '#ffffff',
  border: '1px solid #0000001f',
  textAlign: 'center',
  padding: '4px',
  '& > svg': {
    marginTop: '4px',
  }
});

const AttendanceIcon = ({ session, participant }) => {
  const attendance = participantAttendedSession(participant, session);
  if (attendance === null) {
    return (
      <Tooltip title="Attendance unknown">
        <Typography variant="body1">{NILL}</Typography>
      </Tooltip>
    );
  }
  if (attendance) {
    return (
      <Tooltip title="Attended">
        <CheckCircleIcon color="success"/>
      </Tooltip>
    );
  }
  return (
    <Tooltip title="Did not attend">
      <CancelRoundedIcon color="error"/>
    </Tooltip>
  );
}

export default SessionParticipantsTable;
